if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.buttonLabel = this.submitButton.querySelector('span[data-atc="label"]');
        this.buttonPrice = this.submitButton.querySelector('span[data-atc="price"]');
        this.productInfoRoot = document.querySelector('product-info');
        this.sectionId = this.productInfoRoot?.dataset.section;
        this.sellingPlanInput = null;
        this.qtyInput = this.productInfoRoot ? this.productInfoRoot.querySelector(`#Quantity-${this.sectionId}`) : null;
        this.quantityCount = this.productInfoRoot ? this.productInfoRoot.querySelector('#quantity__count') : null;

        if (this.qtyInput) {
          this.qtyInput.addEventListener('change', (event) => {
            // Clear the content first to force a screen reader update after a short delay.
            if (this.quantityCount) {
              this.quantityCount.textContent = '';
              setTimeout(() => {
                const value = parseInt(event.target.value, 10);
                if (!Number.isNaN(value)) {
                  this.quantityCount.textContent = value === 1 ? '1 item' : `${value} items`;
                }
              }, 50);
            }
            this.updateButtonPrice(event.target.value);
          });
        }

        this.addFormObserver();

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // eslint-disable-next-line class-methods-use-this
      getMoney() {
        const variantId = this.querySelector('input[name="id"]').value;
        const variantPrices = window.SubscriptionWidgetConfig?.formattedPrices[variantId];
        const subscriptionId = this.querySelector('input[name="selling_plan"]')?.value;
        if (subscriptionId && subscriptionId !== '') {
          const variantPrice = variantPrices?.sellingPlans?.[subscriptionId]?.price;
          return variantPrice;
        }
        return variantPrices?.unit;
      }

      syncButtonLabel(text) {
        if (!text) return;
        const price = this.getMoney();
        // eslint-disable-next-line consistent-return
        return { btnLabel: text, btnPrice: price };
      }

      updateButtonPrice(amount) {
        if (!this.buttonPrice) return;
        const price = this.getMoney();
        const priceFloat = price.split('$')[1];
        const updatedPrice = parseFloat(priceFloat, 10) * amount;
        this.buttonPrice.textContent = `$${updatedPrice.toFixed(2)}`;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          const label = this.syncButtonLabel(text);
          if (text) {
            this.buttonLabel.textContent = label.btnLabel;
            this.buttonPrice.textContent = label.btnPrice;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          const textForBtn = window.variantStrings ? window.variantStrings.addToCart : 'Add To Cart';
          const label = this.syncButtonLabel(textForBtn);
          if (text) {
            this.buttonLabel.textContent = label.btnLabel;
            this.buttonPrice.textContent = label.btnPrice;
          }
        }
      }

      addFormObserver() {
        if (this.sellingPlanInput) return;
        const container = document.querySelector('.product-info__purchase'); // or a more specific parent

        if (container) {
          // Create the observer
          const observer = new MutationObserver((mutationsList, obs) => {
            mutationsList.forEach((mutation) => {
              if (mutation.type === 'childList') {
                Array.from(mutation.addedNodes).forEach((node) => {
                  if (node.nodeType === 1 && node.matches?.('input[name="selling_plan"]')) {
                    this.sellingPlanInput = node;
                    this.addSellingPlanListener(node);
                    obs.disconnect(); // stop observing if only needed once
                  }
                });
              }
            });
          });

          // Start observing (looking for new children anywhere inside `container`)
          observer.observe(container, {
            childList: true,
            subtree: true,
          });
        }
      }

      addSellingPlanListener(sellingPlanInput) {
        if (sellingPlanInput) {
          // Use MutationObserver to watch for subscription value changes
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                this.updateButtonPrice(this.qtyInput.value);
              }
            });
          });
          observer.observe(sellingPlanInput, { attributes: true, attributeFilter: ['value'] });
        } else {
          console.log('No selling plan input found');
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    },
  );
}
